import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import SchoolPicker from '../components/SchoolPicker';
import AddressAutocomplete from '../components/AddressAutocomplete';
import Button from '../components/Button';
import './PublicCampRegister.css';

const API = window.location.origin + '/api';
const TSHIRTS = ['YS', 'YM', 'YL', 'AS', 'AM', 'AL', 'AXL', 'A2XL'];

// Human labels for the backend's required-field error so we can tell the
// registrant exactly what's missing instead of a generic failure.
const FIELD_LABELS = {
  first_name: 'First name', last_name: 'Last name', email: 'Email', phone: 'Mobile phone',
  date_of_birth: 'Date of birth', school: 'High school', graduation_year: 'Graduation year',
  home_address: 'Home address', city: 'City', state: 'State', zip_code: 'ZIP',
  tshirt_size: 'Jersey size', emergency_contact_name: 'Emergency contact name',
  emergency_contact_phone: 'Emergency contact phone',
};

function describeError(err) {
  if (err.response?.status === 409) {
    return 'An account already exists for that email or phone. Please sign in to the app to register.';
  }
  const d = err.response?.data?.detail;
  if (d && typeof d === 'object') {
    if (d.code === 'missing_required_fields' && Array.isArray(d.fields)) {
      const names = d.fields.map((k) => FIELD_LABELS[k] || k).join(', ');
      return `Please complete the required field(s): ${names}.`;
    }
    if (d.message) return d.message;
  }
  return typeof d === 'string' ? d : 'Registration failed. Please try again.';
}

// Red asterisk marking a required field.
const Req = () => <span className="pcr-req" aria-hidden="true"> *</span>;

const money = (cents) => (cents / 100).toLocaleString('en-US',
  { style: 'currency', currency: 'USD' });

export default function PublicCampRegisterPage() {
  const { slug } = useParams();
  const [camp, setCamp] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [optionId, setOptionId] = useState(null);
  const [f, setF] = useState({
    first_name: '', last_name: '', email: '', phone: '', date_of_birth: '',
    school: '', school_city: '', school_state: '', school_zip: '',
    graduation_year: '', position_primary: '', twitter_handle: '', instagram_handle: '',
    home_address: '', address_line_2: '', city: '', state: '', zip_code: '',
    tshirt_size: '', medical_notes: '',
    emergency_contact_name: '', emergency_contact_phone: '',
    guardian_first_name: '', guardian_last_name: '', guardian_phone: '', guardian_email: '',
    terms_accepted: false,
    guardian_is_emergency: false,
    two_way_athlete: false,
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const ageFromDob = (dob) => {
    if (!dob) return null;
    const d = new Date(dob + 'T00:00:00');
    if (isNaN(d)) return null;
    const t = new Date();
    let a = t.getFullYear() - d.getFullYear();
    const m = t.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a--;
    return a;
  };

  useEffect(() => {
    document.title = 'Camp Registration | ProZpects';
    axios.get(`${API}/camps/public/${slug}`)
      .then((r) => {
        setCamp(r.data);
        // The visitor may have arrived on a slug this camp has retired (every
        // camp slug gained a -YYYY suffix on 2026-08-30). Move the address bar
        // to the canonical URL without a reload so a shared or bookmarked link
        // starts propagating the current one.
        if (r.data.canonical_slug && r.data.canonical_slug !== slug) {
          window.history.replaceState(null, '', `/camp/${r.data.canonical_slug}/register`);
        }
      })
      .catch(() => setLoadError('This camp is not available for registration.'));
  }, [slug]);

  // Required fields that are always needed (guardian/emergency handled below).
  const missingRequired = () => {
    const ok = (v) => !!(v && String(v).trim());
    const out = [];
    if ((camp.price_options || []).length && !optionId) {
      out.push('A registration option');
    }
    [
      ['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email'],
      ['phone', 'Mobile phone'], ['date_of_birth', 'Date of birth'], ['school', 'High school'],
      ['graduation_year', 'Graduation year'], ['home_address', 'Home address'],
      ['city', 'City'], ['state', 'State'], ['zip_code', 'ZIP'], ['tshirt_size', 'Jersey size'],
    ].forEach(([k, label]) => { if (!ok(f[k])) out.push(label); });
    const yrs = ageFromDob(f.date_of_birth);
    const minor = yrs !== null && yrs < 13;
    if (f.guardian_is_emergency || minor) {
      if (!ok(f.guardian_first_name)) out.push('Guardian first name');
      if (!ok(f.guardian_last_name)) out.push('Guardian last name');
      if (!ok(f.guardian_phone)) out.push('Guardian mobile');
      if (minor && !ok(f.guardian_email)) out.push('Guardian email');
    }
    if (!f.guardian_is_emergency) {
      if (!ok(f.emergency_contact_name)) out.push('Emergency contact name');
      if (!ok(f.emergency_contact_phone)) out.push('Emergency contact phone');
    }
    if (!f.terms_accepted) out.push('Agree to the Privacy Policy and Terms');
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const missing = missingRequired();
    if (missing.length) {
      setError(`Please complete: ${missing.join(', ')}.`);
      return;
    }
    setSubmitting(true);
    try {
      const guardianName = `${f.guardian_first_name} ${f.guardian_last_name}`.trim();
      const emName = f.guardian_is_emergency ? guardianName : f.emergency_contact_name;
      const emPhone = f.guardian_is_emergency ? f.guardian_phone : f.emergency_contact_phone;
      const payload = {
        ...f,
        guardian_name: guardianName,
        graduation_year: f.graduation_year ? parseInt(f.graduation_year, 10) : null,
        emergency_contact_name: emName,
        emergency_contact_phone: emPhone,
        price_option_id: optionId,
        // Never sent as a bare `f` value: a camp that doesn't offer the ATH
        // election must not receive a stale true, and the server refuses it
        // there anyway.
        two_way_athlete: !!(camp.ath_option_enabled && f.two_way_athlete),
        // Not sent at all: the SERVER resolves the position from the chosen
        // option, because a request could otherwise claim any position. An
        // option carrying exactly one slug records it; one covering several
        // records nothing, since we genuinely do not know which the athlete is.
      };
      const r = await axios.post(`${API}/camps/public/${slug}/register`, payload);
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        window.location.href = `/camp/${slug}/success`;
      }
    } catch (err) {
      setError(describeError(err));
      setSubmitting(false);
    }
  };

  if (loadError) return <div className="pcr-shell"><div className="pcr-card"><p>{loadError}</p></div></div>;
  if (!camp) return <div className="pcr-shell"><div className="pcr-card"><p>Loading…</p></div></div>;

  const age = ageFromDob(f.date_of_birth);
  const isMinor = age !== null && age < 13;
  // Guardian becomes required when they're the emergency contact or the athlete is under 13.
  const guardianRequired = f.guardian_is_emergency || isMinor;

  const options = camp.price_options || [];
  // Sits directly under whichever control IS the position on this camp: the
  // option picker when the camp prices by position, the "Primary position"
  // field when it doesn't. ATH is a modifier on that choice — the athlete
  // still registers at, and pays for, one position.
  const athCheckbox = camp.ath_option_enabled ? (
    <label className="pcr-terms pcr-ath" data-testid="camp_ath_two_way">
      <input type="checkbox" checked={!!f.two_way_athlete}
        onChange={(e) => setF((p) => ({ ...p, two_way_athlete: e.target.checked }))} />
      <span><strong>ATH</strong> (I intend to be tested on offense and defense)</span>
    </label>
  ) : null;
  const selected = options.find((o) => o.id === optionId) || null;
  // A camp with no options charges its base price, exactly as before this
  // feature existed. With options, nothing is chargeable until one is picked.
  const priceCents = options.length ? (selected ? selected.price_cents : null)
                                    : camp.price_cents;
  const price = priceCents === null ? null : money(priceCents);
  const dateStr = camp.date ? new Date(camp.date).toLocaleDateString('en-US',
    { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="pcr-shell">
      <div className="pcr-card">
        <img src="/logo-wordmark.png" alt="ProZpects" className="pcr-logo" />
        <header className="pcr-hero">
          <h1>{camp.name}</h1>
          <p>{dateStr}{camp.venue ? ` · ${camp.venue}` : ''} · {camp.location}</p>
          <p className="pcr-hero__meta">
            {options.length === 0 && <strong>{price}</strong>}
            {camp.free_sub_months > 0 && <span> · includes {camp.free_sub_months} months free ProZpects</span>}
          </p>
        </header>

        {error && <div className="pcr-error" role="alert">{error}</div>}

        <form onSubmit={submit} className="pcr-form">
          <p className="pcr-reqnote">Fields marked <span className="pcr-req">*</span> are required.</p>
          {options.length > 0 && (
            <>
              <div className="pcr-section">Choose your registration</div>
              <div className="pcr-options" data-testid="camp_price_options" role="radiogroup"
                   aria-label="Registration options">
                {options.map((o) => (
                  <label key={o.id}
                         className={`pcr-option${optionId === o.id ? ' pcr-option--on' : ''}`}>
                    <input type="radio" name="price_option" value={o.id}
                           checked={optionId === o.id}
                           onChange={() => setOptionId(o.id)} />
                    <span className="pcr-option__body">
                      <span className="pcr-option__name">{o.name}</span>
                      {o.description && (
                        <span className="pcr-option__desc">{o.description}</span>
                      )}
                      {(o.checkin_time || o.field_time) && (
                        <span className="pcr-option__time">
                          {/* Joined rather than concatenated with a separator so
                              an option carrying only one of the two does not
                              render a dangling "·". */}
                          {[
                            o.checkin_time && `Check-in ${o.checkin_time}`,
                            o.field_time && `On field ${o.field_time}`,
                          ].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {typeof o.spots_remaining === 'number' && (
                        <span className="pcr-option__left">{o.spots_remaining} spots left</span>
                      )}
                    </span>
                    <span className="pcr-option__price">{money(o.price_cents)}</span>
                  </label>
                ))}
              </div>
              {athCheckbox}
            </>
          )}
          <div className="pcr-section">Athlete</div>
          <div className="pcr-row">
            <label>First name<Req /><input value={f.first_name} onChange={set('first_name')} required /></label>
            <label>Last name<Req /><input value={f.last_name} onChange={set('last_name')} required /></label>
          </div>
          <div className="pcr-row">
            <label>Email<Req /><input type="email" value={f.email} onChange={set('email')} required /></label>
            <label>Mobile phone<Req /><input type="tel" value={f.phone} onChange={set('phone')} required /></label>
          </div>
          <div className="pcr-row">
            <label>Date of birth<Req /><input type="date" value={f.date_of_birth} onChange={set('date_of_birth')}
              max={new Date().toISOString().slice(0, 10)} required /></label>
            <label>Graduation year<Req /><input type="number" value={f.graduation_year} onChange={set('graduation_year')} placeholder="2028" /></label>
          </div>

          <div className="pcr-section">School &amp; athletics</div>
          <label>High school<Req />
            <SchoolPicker value={f.school} city={f.school_city} state={f.school_state} zip={f.school_zip}
              onChange={(s) => setF((p) => ({ ...p, school: s.name, school_city: s.city, school_state: s.state, school_zip: s.zip }))} />
          </label>
          {/* Only when the camp does NOT price by position. With options, the
              picker above IS the position, and asking twice invites the two to
              disagree. Without options there is no picker, so this stays the
              only place a position is collected. */}
          {options.length === 0 && (
            <>
              <div className="pcr-row">
                <label>Primary position<input value={f.position_primary} onChange={set('position_primary')} /></label>
              </div>
              {athCheckbox}
            </>
          )}
          <div className="pcr-row">
            <label>X / Twitter<input value={f.twitter_handle} onChange={set('twitter_handle')} placeholder="@handle" /></label>
            <label>Instagram<input value={f.instagram_handle} onChange={set('instagram_handle')} placeholder="@handle" /></label>
          </div>
          <p style={{ fontSize: 13, color: '#6b6b6b', margin: '-4px 0 12px' }}>
            Add your social accounts so we can promote you to coaches. Optional.
          </p>

          <div className="pcr-section">Address</div>
          <label>Home address<Req />
            <AddressAutocomplete value={f.home_address} public
              onSelect={(d) => setF((p) => ({ ...p, home_address: d.home_address, city: d.city, state: d.state, zip_code: d.zip_code }))}
              onManualChange={(e) => setF((p) => ({ ...p, home_address: e.target.value }))}
              placeholder="Start typing your address…" />
          </label>
          <div className="pcr-row">
            <label>City<Req /><input value={f.city} onChange={set('city')} /></label>
            <label>State<Req /><input value={f.state} onChange={set('state')} /></label>
            <label>ZIP<Req /><input value={f.zip_code} onChange={set('zip_code')} /></label>
          </div>

          <div className="pcr-section">Guardian</div>
          <div className="pcr-row">
            <label>Guardian first name{guardianRequired && <Req />}<input value={f.guardian_first_name} onChange={set('guardian_first_name')} /></label>
            <label>Guardian last name{guardianRequired && <Req />}<input value={f.guardian_last_name} onChange={set('guardian_last_name')} /></label>
          </div>
          <div className="pcr-row">
            <label>Guardian mobile{guardianRequired && <Req />}<input type="tel" value={f.guardian_phone} onChange={set('guardian_phone')} /></label>
            <label>Guardian email{isMinor && <Req />}<input type="email" value={f.guardian_email} onChange={set('guardian_email')} /></label>
          </div>
          <label className="pcr-terms" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={!!f.guardian_is_emergency}
              onChange={(e) => setF((p) => ({ ...p, guardian_is_emergency: e.target.checked }))} />
            <span>Guardian is the emergency contact</span>
          </label>
          {!f.guardian_is_emergency && (
            <div className="pcr-row">
              <label>Emergency contact name<Req /><input value={f.emergency_contact_name} onChange={set('emergency_contact_name')} /></label>
              <label>Emergency contact phone<Req /><input type="tel" value={f.emergency_contact_phone} onChange={set('emergency_contact_phone')} /></label>
            </div>
          )}

          <div className="pcr-section">Camp logistics</div>
          <div className="pcr-row">
            <label>Jersey size<Req />
              <select value={f.tshirt_size} onChange={set('tshirt_size')}>
                <option value="">Select…</option>
                {TSHIRTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <label>Medical / allergy notes (optional)
            <textarea value={f.medical_notes} onChange={set('medical_notes')} rows={3} />
          </label>

          <label className="pcr-terms">
            <input type="checkbox" checked={f.terms_accepted}
              onChange={(e) => setF((p) => ({ ...p, terms_accepted: e.target.checked }))} />
            <span>I agree to the <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a> and Terms of Use.</span>
          </label>

          <p className="pcr-sms-consent" style={{ fontSize: 12.5, lineHeight: 1.45, marginTop: 8, color: '#666' }}>
            By registering with a mobile number, you agree that ProZpects may text you camp and
            account-related messages. Message frequency varies. Msg &amp; data rates may apply.
            Reply STOP to opt out, HELP for help. See our{' '}
            <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </p>

          <Button type="submit" variant="primary" fullWidth
                  disabled={submitting || price === null} style={{ marginTop: 18 }}>
            {submitting ? 'Processing…'
              : price ? `Continue to payment · ${price}` : 'Choose an option to continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
